import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AccessPolicyUpdate,
  type AllActiveBackfillsResponse,
  type AllTreeCoverageResponse,
  cancelLibraryTreeBackfill,
  clearLibraryAccessPolicy,
  createLibrary,
  deleteLibrary,
  getAllActiveBackfills,
  getAllTreeCoverage,
  getLibraryAccessPolicy,
  getLibraryBaseAccessPolicy,
  getLibraryStats,
  getLibraryTreeBackfillStatus,
  getLibraryTreeCoverage,
  type LibraryConfig,
  type LibraryTreeBackfillStatusResponse,
  restartLibraryTreeBackfill,
  retryIndexing,
  startLibraryTreeBackfill,
  updateLibraryAccessPolicy,
  updateLibraryBaseAccessPolicy,
  updateLibraryConfig,
} from "@/api/admin-api"
import { api } from "@/lib/api-client"

import { adminQueryKeys } from "../admin-query-keys"
import { FAST_STALE_TIME, GC_TIME } from "./_shared"

export function useAdminLibraryStatsQuery(libraryId: string | null) {
  return useQuery({
    queryKey: adminQueryKeys.libraryStats(libraryId),
    queryFn: () => getLibraryStats(libraryId!),
    enabled: !!libraryId,
    staleTime: 0, // Always refetch to get fresh diffs
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

// Library Management

export function useAdminLibrariesQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "navigator", "libraries"],
    queryFn: async () => {
      const response = await api.get("/admin/navigator/libraries")
      const data = response.data || response
      // Ensure we always return an array
      return Array.isArray(data) ? data : []
    },
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

/** @deprecated Use useUpdateLibrarySearchEnabledMutation instead */
export function useUpdateLibraryStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ libraryId, enabled }: { libraryId: string; enabled: boolean }) => {
      const response = await api.put(`/admin/libraries/${libraryId}/status`, { enabled })
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update library status")
      }
      return data
    },
    onMutate: async ({ libraryId, enabled }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["admin", "navigator", "libraries"] })

      // Snapshot the previous value
      const previousLibraries = queryClient.getQueryData<Array<{ id: string; enabled: boolean }>>([
        "admin",
        "navigator",
        "libraries",
      ])

      // Optimistically update to the new value
      if (previousLibraries) {
        queryClient.setQueryData(
          ["admin", "navigator", "libraries"],
          previousLibraries.map((lib) => (lib.id === libraryId ? { ...lib, enabled } : lib))
        )
      }

      // Return a context object with the snapshotted value
      return { previousLibraries }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousLibraries) {
        queryClient.setQueryData(["admin", "navigator", "libraries"], context.previousLibraries)
      }
    },
    // Don't refetch on success - we already updated optimistically
    // Only refetch if there's an error to ensure consistency
  })
}

export function useUpdateLibrarySearchEnabledMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ libraryId, enabled }: { libraryId: string; enabled: boolean }) => {
      const response = await api.put(`/admin/libraries/${libraryId}/search-enabled`, { enabled })
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update library search status")
      }
      return data
    },
    onMutate: async ({ libraryId, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "navigator", "libraries"] })

      const previousLibraries = queryClient.getQueryData<
        Array<{ id: string; search_enabled: boolean }>
      >(["admin", "navigator", "libraries"])

      if (previousLibraries) {
        queryClient.setQueryData(
          ["admin", "navigator", "libraries"],
          previousLibraries.map((lib) =>
            lib.id === libraryId ? { ...lib, search_enabled: enabled } : lib
          )
        )
      }

      return { previousLibraries }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLibraries) {
        queryClient.setQueryData(["admin", "navigator", "libraries"], context.previousLibraries)
      }
    },
  })
}

export function useUpdateLibraryIndexingEnabledMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ libraryId, enabled }: { libraryId: string; enabled: boolean }) => {
      const response = await api.put(`/admin/libraries/${libraryId}/indexing-enabled`, { enabled })
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update library indexing status")
      }
      return data
    },
    onMutate: async ({ libraryId, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "navigator", "libraries"] })

      const previousLibraries = queryClient.getQueryData<
        Array<{ id: string; indexing_enabled: boolean }>
      >(["admin", "navigator", "libraries"])

      if (previousLibraries) {
        queryClient.setQueryData(
          ["admin", "navigator", "libraries"],
          previousLibraries.map((lib) =>
            lib.id === libraryId ? { ...lib, indexing_enabled: enabled } : lib
          )
        )
      }

      return { previousLibraries }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLibraries) {
        queryClient.setQueryData(["admin", "navigator", "libraries"], context.previousLibraries)
      }
    },
  })
}

export function useUpdateLibraryConfigMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      libraryId,
      config,
    }: {
      libraryId: string
      config: Partial<LibraryConfig>
    }) => {
      return await updateLibraryConfig(libraryId, config)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "libraries", variables.libraryId],
      })
    },
  })
}

export function useAdminLibraryAccessPolicyQuery(libraryId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "libraries", libraryId, "access"],
    queryFn: () => getLibraryAccessPolicy(libraryId ?? ""),
    enabled: enabled && !!libraryId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateLibraryAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      libraryId,
      policy,
    }: {
      libraryId: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateLibraryAccessPolicy(libraryId, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "libraries", variables.libraryId, "access"],
      })
    },
  })
}

export function useAdminLibraryBaseAccessPolicyQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "libraries", "base", "access"],
    queryFn: () => getLibraryBaseAccessPolicy(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateLibraryBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ policy }: { policy: AccessPolicyUpdate }) => {
      return await updateLibraryBaseAccessPolicy(policy)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "libraries", "base", "access"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
    },
  })
}

export function useClearLibraryAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ libraryId }: { libraryId: string }) => {
      return await clearLibraryAccessPolicy(libraryId)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "libraries", variables.libraryId, "access"],
      })
    },
  })
}

export function useCreateLibraryMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (config: LibraryConfig) => {
      return await createLibrary(config)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
    },
  })
}

export function useDeleteLibraryMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      libraryId,
      deleteIndex = false,
      deleteTable = false,
      deleteContainer = false,
    }: {
      libraryId: string
      deleteIndex?: boolean
      deleteTable?: boolean
      deleteContainer?: boolean
    }) => {
      return await deleteLibrary(libraryId, {
        deleteIndex,
        deleteTable,
        deleteContainer,
      })
    },
    onMutate: async ({ libraryId }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "navigator", "libraries"] })

      const previousLibraries = queryClient.getQueryData<Array<{ id: string }>>([
        "admin",
        "navigator",
        "libraries",
      ])

      if (previousLibraries) {
        queryClient.setQueryData(
          ["admin", "navigator", "libraries"],
          previousLibraries.filter((lib) => lib.id !== libraryId)
        )
      }

      return { previousLibraries }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLibraries) {
        queryClient.setQueryData(["admin", "navigator", "libraries"], context.previousLibraries)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "navigator", "libraries"] })
    },
  })
}

export function useRetryIndexingMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ libraryId, filenames }: { libraryId: string; filenames?: string[] }) => {
      return await retryIndexing({ library_id: libraryId, filenames })
    },
    onSuccess: (_data, variables) => {
      // Invalidate job history to show the new retry job
      void queryClient.invalidateQueries({ queryKey: ["admin", "indexing", "jobs"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "libraries", variables.libraryId, "stats"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "libraries", variables.libraryId, "documents"],
      })
    },
  })
}

// =============================================================================
// Tree-walk coverage + backfill (admin)
// =============================================================================

export function useLibraryTreeCoverageQuery(libraryId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.libraryTreeCoverage(libraryId),
    queryFn: () => getLibraryTreeCoverage(libraryId!),
    enabled: enabled && !!libraryId,
    staleTime: 60_000,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Single batch query for tree coverage across every library. Replaces the
 * per-row ``useLibraryTreeCoverageQuery`` for the libraries-table mount
 * because the Trees column is always visible — gating the per-row query on
 * row-expand left the badge looking broken.
 *
 * Returns a map ``{library_id: {total, with_tree, without_tree, coverage_pct}}``.
 */
export function useAllTreeCoverageQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.libraryTreeCoverageAll(),
    queryFn: () => getAllTreeCoverage(),
    select: (res: AllTreeCoverageResponse) => res.coverage,
    enabled,
    staleTime: 60_000,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Single batch query for all libraries with an active tree backfill.
 * Replaces the per-row ``useLibraryTreeBackfillActiveQuery`` to avoid the
 * N+1 network fan-out on dashboard mount.
 *
 * Returns a map ``{library_id: job_id}``.
 */
export function useAllActiveBackfillsQuery(enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
    queryFn: () => getAllActiveBackfills(),
    select: (res: AllActiveBackfillsResponse) => res.active,
    enabled,
    staleTime: 5_000,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    refetchInterval: 10_000,
  })
}

export function useLibraryTreeBackfillStatusQuery(
  libraryId: string | null,
  jobId: string | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: adminQueryKeys.libraryTreeBackfillStatus(libraryId, jobId),
    queryFn: () => getLibraryTreeBackfillStatus(libraryId!, jobId!),
    enabled: enabled && !!libraryId && !!jobId,
    refetchInterval: (query) => {
      if (query.state.error) {
        // Back off on poll errors instead of stopping silently; the UI shows
        // an error banner and still lets the user cancel or retry.
        return 10_000
      }
      const data = query.state.data as LibraryTreeBackfillStatusResponse | undefined
      // Terminal payloads (no job row): stop polling.
      if (data && !("job" in data)) return false
      const status = data && "job" in data ? String(data.job.status ?? "") : ""
      if (status === "running" || status === "cancel_requested" || status === "pending") return 2000
      return false
    },
    retry: 3,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useStartLibraryTreeBackfillMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { libraryId: string; batchSize?: number; overwrite?: boolean }) =>
      startLibraryTreeBackfill(args.libraryId, {
        batch_size: args.batchSize,
        overwrite: args.overwrite,
      }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
      })
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeCoverage(vars.libraryId),
      })
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeCoverageAll(),
      })
    },
  })
}

export function useCancelLibraryTreeBackfillMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { libraryId: string; jobId: string }) =>
      cancelLibraryTreeBackfill(args.libraryId, args.jobId),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeBackfillStatus(vars.libraryId, vars.jobId),
      })
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
      })
    },
  })
}

export function useRestartLibraryTreeBackfillMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      libraryId: string
      jobId: string
      batchSize?: number
      overwrite?: boolean
    }) =>
      restartLibraryTreeBackfill(args.libraryId, args.jobId, {
        batch_size: args.batchSize,
        overwrite: args.overwrite,
      }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
      })
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeCoverage(vars.libraryId),
      })
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.libraryTreeCoverageAll(),
      })
    },
  })
}
